/**
 * 📷 실사 사진 모아 보기 — 데스크톱 이력 표(주간 확인 · 분기 실사)에서 품목 하나의 사진을 크게 (v2.224.0)
 *
 * 보기 전용 창 — 닫기는 ✕·ESC 로(배경 클릭으로 닫지 않는다 · 다른 창들과 같은 규칙 · check:modal).
 * 지우기는 모바일 실사 화면에서만(사진 띠 ✕) — 데스크톱에서 지우는 길을 두면 두 벌이 된다.
 */

import { useQuery } from '@tanstack/react-query';
import * as api from '../lib/stock-audit-api';
import type { PhotoKey } from '../lib/stock-audit-api';
import { photoQueryKey, openPhoto } from './StockPhotoStrip';
import { dateTimeLabel } from '../lib/date-label';
import { useDialogLayer } from '../lib/dialog-stack';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  photoKey: PhotoKey;
  itemCode: string;
  itemName: string | null;
  title?: string;
  onClose: () => void;
}

export function StockPhotoGallery({ photoKey, itemCode, itemName, title, onClose }: Props) {
  const q = useQuery({
    queryKey: photoQueryKey(photoKey, itemCode),
    queryFn: () => api.listPhotos({ ...photoKey, itemCode } as PhotoKey),
    staleTime: 30_000,
  });
  // 보기 창 — ESC 로 닫힌다 · 맨 위 창만 받는다 (v2.231.0 · H-4)
  useDialogLayer(true, onClose);
  const photos = q.data?.photos ?? [];

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 truncate">📷 {itemName ?? itemCode}</div>
            <div className="text-[11px] text-slate-500 truncate">{itemCode}{title ? ` · ${title}` : ''}</div>
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>
        <div className="p-4 overflow-y-auto">
          {q.isLoading && <div className="text-sm text-slate-400 py-8 text-center"><LoadingLines /></div>}
          {q.isError && <div className="text-sm text-red-600 py-4">{(q.error as Error).message}</div>}
          {!q.isLoading && !q.isError && photos.length === 0 && (
            <div className="text-sm text-slate-400 py-8 text-center">이 품목에 올린 사진이 없습니다.</div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((p) => (
              <figure key={p.id} className="border border-slate-200 rounded overflow-hidden bg-slate-50">
                <button type="button" onClick={() => openPhoto(p)} className="block w-full aspect-[4/3] bg-slate-100" title="크게 보기">
                  <img src={api.photoFileUrl(p.id)} alt={p.filename} loading="lazy" className="w-full h-full object-cover" />
                </button>
                <figcaption className="px-2 py-1.5 text-[11px] text-slate-600">
                  {dateTimeLabel(p.takenAt)} · {p.takenByName}
                  <div className="text-[11px] text-slate-500 truncate" title={p.nasPath}>{p.nasPath}</div>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            사진은 NAS 「4. 기술지원부\포털 재고실사 사진」에 있고, 포털이 그때그때 읽어 보여 줍니다. 지우기는 모바일 실사 화면에서.
          </p>
        </div>
      </div>
    </div>
  );
}
